import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
// Portal pages don't need separate header/footer
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { useWhiteLabel, getCompanyName, getContactEmail, getAddress } from '../../contexts/WhiteLabelContext';

const PrivacyPolicy: React.FC = () => {
  const whiteLabel = useWhiteLabel();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const companyName = getCompanyName(whiteLabel);
  const address = getAddress(whiteLabel);
  const privacyEmail = getContactEmail(whiteLabel, 'privacy');
  const supportEmail = getContactEmail(whiteLabel, 'support');
  const legalEmail = getContactEmail(whiteLabel, 'legal');
  const fairhousingEmail = getContactEmail(whiteLabel, 'fairhousing');
  const dpoEmail = privacyEmail; // Use same as privacy email for DPO
  const supportPhone = whiteLabel.contact?.phone || '1-602-860-6110';
  const fullAddress = `${address.street}, ${address.city}, ${address.state} ${address.zip}`;

  return (
    <div className="policy-page">
      
      <Container className="py-5">
        <Row>
          <Col lg={10} className="mx-auto">
            <Link to="/" className="btn btn-link mb-4">
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
            </Link>
            
            <Card className="policy-content">
              <Card.Body className="p-5">
                <div className="text-center mb-5">
                  <FontAwesomeIcon icon={faShieldAlt} size="3x" className="text-primary mb-3" />
                  <h1>Privacy Policy</h1>
                  <p className="text-muted">Last Updated: {new Date().toLocaleDateString()}</p>
                </div>

                <div className="policy-section">
                  <h2>Welcome to {companyName}</h2>
                  <p>
                    At {companyName} ("we," "us," or "our"), a licensed real estate brokerage, 
                    we are committed to protecting your privacy and ensuring the security of your 
                    personal information. This Privacy Policy explains how we collect, use, disclose, 
                    and safeguard your information when you use our website and 
                    related services. Our property search and matching technology is powered by 
                    KairosIQ AI, but all real estate services and listings are provided directly 
                    by {companyName}.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>1. Information We Collect</h3>
                  
                  <h4>1.1 Information You Provide</h4>
                  <ul>
                    <li><strong>Account Information:</strong> Name, email address, phone number when you create an account</li>
                    <li><strong>Search Preferences:</strong> Property preferences, budget, location preferences, and search criteria</li>
                    <li><strong>Communications:</strong> Messages you send through our chat interface or to agents</li>
                    <li><strong>Feedback:</strong> Survey responses, reviews, and other feedback you provide</li>
                  </ul>

                  <h4>1.2 Information Collected Automatically</h4>
                  <ul>
                    <li><strong>Usage Data:</strong> Pages visited, features used, search queries, and interaction patterns</li>
                    <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
                    <li><strong>Location Data:</strong> Approximate location based on IP address or with your permission</li>
                    <li><strong>Cookies:</strong> We use cookies to enhance your experience and remember preferences</li>
                  </ul>

                  <h4>1.3 AI-Processed Information (via KairosIQ Technology)</h4>
                  <ul>
                    <li><strong>Behavioral Patterns:</strong> How you interact with property listings and searches</li>
                    <li><strong>Preference Learning:</strong> AI-derived insights about your property preferences</li>
                    <li><strong>Match Scoring:</strong> Compatibility scores between you and properties</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>2. How We Use Your Information</h3>
                  <p>We use your information to:</p>
                  <ul>
                    <li>Provide personalized property recommendations</li>
                    <li>Connect you with real estate agents</li>
                    <li>Improve our AI matching algorithms</li>
                    <li>Send property alerts and updates (with your consent)</li>
                    <li>Respond to your inquiries and provide customer support</li>
                    <li>Comply with legal obligations and prevent fraud</li>
                    <li>Analyze usage patterns to improve our services</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>3. AI and Automated Decision-Making</h3>
                  <p>
                    Our platform uses AI technology to enhance your property search experience. 
                    Here's how it works:
                  </p>
                  <ul>
                    <li><strong>Property Matching:</strong> AI analyzes your preferences to suggest relevant properties</li>
                    <li><strong>Natural Language Processing:</strong> Our chat interface understands your queries in plain English</li>
                    <li><strong>Predictive Analytics:</strong> We predict properties you might be interested in based on behavior</li>
                    <li><strong>Human Oversight:</strong> All AI recommendations can be reviewed by human agents</li>
                  </ul>
                  <p>
                    <strong>Your Rights:</strong> You can opt-out of AI processing, request human review of 
                    decisions, and access explanations of how recommendations are made.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>4. Information Sharing</h3>
                  <p>We share your information only in these circumstances:</p>
                  <ul>
                    <li><strong>Real Estate Agents:</strong> When you request to contact an agent about a property</li>
                    <li><strong>Service Providers:</strong> Companies that help us operate our platform (under strict confidentiality)</li>
                    <li><strong>Legal Requirements:</strong> When required by law or to protect rights and safety</li>
                    <li><strong>Business Transfers:</strong> In connection with a merger or acquisition (with notice to you)</li>
                    <li><strong>With Your Consent:</strong> When you explicitly agree to specific sharing</li>
                  </ul>
                  <p>
                    <strong>We Never:</strong> Sell your personal information to third parties for marketing purposes.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>5. Data Security</h3>
                  <p>We protect your information through:</p>
                  <ul>
                    <li>Industry-standard encryption (TLS/SSL) for data transmission</li>
                    <li>Secure data centers with 24/7 monitoring</li>
                    <li>Regular security audits and penetration testing</li>
                    <li>Access controls and employee training</li>
                    <li>Incident response procedures</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>6. Your Privacy Rights</h3>
                  <p>You have the right to:</p>
                  <ul>
                    <li><strong>Access:</strong> Request a copy of your personal information</li>
                    <li><strong>Correct:</strong> Update inaccurate or incomplete information</li>
                    <li><strong>Delete:</strong> Request deletion of your account and associated data</li>
                    <li><strong>Port:</strong> Receive your data in a portable format</li>
                    <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                    <li><strong>Restrict:</strong> Limit how we process your information</li>
                  </ul>
                  <p>
                    To exercise these rights, contact us at <a href={`mailto:${privacyEmail}`}>{privacyEmail}</a>
                  </p>
                </div>

                <div className="policy-section">
                  <h3>7. Text Messaging and Communications Consent</h3>
                  
                  <h4>SMS/Text Messaging Opt-In</h4>
                  <p>
                    By providing your phone number and opting in to text messaging, you expressly 
                    consent to receive text messages from {companyName} and our agents regarding:
                  </p>
                  <ul>
                    <li>Property alerts and updates</li>
                    <li>Appointment reminders and scheduling</li>
                    <li>Important real estate transaction updates</li>
                    <li>Market insights and opportunities</li>
                  </ul>
                  
                  <Alert variant="info">
                    <h6>Important SMS Terms:</h6>
                    <ul className="mb-0">
                      <li>Message frequency varies based on your activity and preferences</li>
                      <li>Message and data rates may apply</li>
                      <li>Text STOP to cancel at any time</li>
                      <li>Text HELP for assistance</li>
                      <li>We will never share your phone number with third parties for marketing</li>
                    </ul>
                  </Alert>

                  <p>
                    <strong>Carriers Supported:</strong> We support all major US carriers including 
                    AT&T, Verizon, T-Mobile, Sprint, and others. Carrier message and data rates apply.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>8. Cookies and Tracking</h3>
                  <p>We use cookies to:</p>
                  <ul>
                    <li>Remember your login and preferences</li>
                    <li>Analyze site traffic and usage</li>
                    <li>Personalize content and ads</li>
                    <li>Enable social media features</li>
                  </ul>
                  <p>
                    You can control cookies through your browser settings. Note that disabling 
                    cookies may limit functionality.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>9. Children's Privacy</h3>
                  <p>
                    Our services are not intended for children under 18. We do not knowingly 
                    collect information from minors. If you believe we have collected information 
                    from a child, please contact us immediately.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>10. International Users</h3>
                  <p>
                    If you access our services from outside the United States, please note that 
                    your information may be transferred to and processed in the US. We comply 
                    with applicable data protection laws including GDPR for EU residents and 
                    CCPA for California residents.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>11. Fair Housing Commitment</h3>
                  <p>
                    {companyName} is committed to fair housing practices. Our AI systems are 
                    designed and audited to prevent discrimination based on race, color, religion, 
                    sex, national origin, disability, or familial status.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>12. Changes to This Policy</h3>
                  <p>
                    We may update this policy periodically. We will notify you of material 
                    changes via email or prominent website notice. Continued use after changes 
                    indicates acceptance.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>13. Contact Us</h3>
                  <p>For questions about this Privacy Policy or our practices, contact us:</p>
                  <ul className="list-unstyled">
                    <li><strong>Email:</strong> <a href={`mailto:${privacyEmail}`}>{privacyEmail}</a></li>
                    <li><strong>Phone:</strong> {supportPhone}</li>
                    <li><strong>Mail:</strong> {companyName}, {fullAddress}</li>
                    <li><strong>Data Protection Officer:</strong> <a href={`mailto:${dpoEmail}`}>{dpoEmail}</a></li>
                  </ul>
                </div>

                <div className="text-center mt-5">
                  <Link to="/terms-of-service" className="btn btn-outline-primary">
                    View Terms of Service
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>


      <style>{`
        .policy-page {
          background-color: #f8f9fa;
          min-height: 100vh;
        }

        .policy-content {
          box-shadow: 0 0 20px rgba(0,0,0,0.1);
          border: none;
        }

        .policy-section {
          margin-bottom: 40px;
        }

        .policy-section h2 {
          color: #333;
          margin-bottom: 20px;
          font-size: 28px;
        }

        .policy-section h3 {
          color: #333;
          margin-bottom: 15px;
          margin-top: 30px;
          font-size: 22px;
        }

        .policy-section h4 {
          color: #555;
          margin-bottom: 10px;
          margin-top: 20px;
          font-size: 18px;
        }

        .policy-section p {
          line-height: 1.7;
          color: #555;
        }

        .policy-section ul {
          line-height: 1.8;
          color: #555;
        }

        .policy-section ul li {
          margin-bottom: 8px;
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;